'use client';

import { useState, useCallback } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  IconButton,
  Button,
  Tooltip,
  Badge,
  LegacyDialog,
  Input,
  Textarea,
  Switch,
  RadioGroup,
} from '@terra/ui-design-system';
import {
  Alert01Icon,
  Copy01Icon,
  Edit02Icon,
  InformationCircleIcon,
  Add01Icon,
  Shield01Icon,
  Delete02Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import {
  useExclusionRulesStore,
  type ExclusionRule,
} from '../../store/exclusionRulesStore';

type ExclusionRulesManagerProps = {
  onRulesChange?: () => void;
};

export function ExclusionRulesManager({
  onRulesChange,
}: ExclusionRulesManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<ExclusionRule | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { rules, addRule, updateRule, deleteRule, toggleRule, duplicateRule } =
    useExclusionRulesStore();
  const enabledCount = rules.filter((r) => r.enabled).length;
  const handleToggle = useCallback(
    (rule: ExclusionRule) => {
      toggleRule(rule.id);
      onRulesChange?.();
    },
    [toggleRule, onRulesChange],
  );
  const handleDuplicate = useCallback(
    (rule: ExclusionRule) => {
      const newRule = duplicateRule(rule.id);

      if (newRule) {
        setEditingRule(newRule);
      }
    },
    [duplicateRule],
  );
  const handleDelete = useCallback(
    (rule: ExclusionRule) => {
      deleteRule(rule.id);
      onRulesChange?.();
    },
    [deleteRule, onRulesChange],
  );
  const handleEdit = useCallback((rule: ExclusionRule) => {
    setEditingRule(rule);
  }, []);
  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setEditingRule({
      id: '',
      name: '',
      description: '',
      type: 'exclude',
      pattern: '',
      isRegex: false,
      enabled: true,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, []);
  const handleSave = useCallback(
    (rule: ExclusionRule) => {
      if (isCreating) {
        addRule({
          name: rule.name,
          description: rule.description,
          type: rule.type,
          pattern: rule.pattern,
          isRegex: rule.isRegex,
          enabled: rule.enabled,
        });
      } else {
        updateRule(rule.id, {
          name: rule.name,
          description: rule.description,
          type: rule.type,
          pattern: rule.pattern,
          isRegex: rule.isRegex,
          enabled: rule.enabled,
        });
      }

      setEditingRule(null);
      setIsCreating(false);
      onRulesChange?.();
    },
    [isCreating, addRule, updateRule, onRulesChange],
  );
  const handleCancel = useCallback(() => {
    setEditingRule(null);
    setIsCreating(false);
  }, []);

  return (
    <>
      <Tooltip title="Manage proxy exclusion rules">
        <IconButton
          aria-label="Exclusion rules"
          size="xs"
          variant="ghost"
          onClick={() => setIsOpen(true)}
        >
          <HStack gap="1">
            <Shield01Icon size={14} />
            {enabledCount > 0 && (
              <Badge size="sm" variant="fill">
                {enabledCount}
              </Badge>
            )}
          </HStack>
        </IconButton>
      </Tooltip>

      <LegacyDialog.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="700px">
            <VStack gap="4" p="6" alignItems="stretch">
              {/* Header */}
              <HStack justifyContent="space-between" alignItems="center">
                <VStack gap="1" alignItems="flex-start">
                  <LegacyDialog.Title>Proxy Exclusion Rules</LegacyDialog.Title>
                  <LegacyDialog.Description>
                    <Text fontSize="xs" color="fg.muted">
                      Requests matching these rules will be excluded from
                      history
                    </Text>
                  </LegacyDialog.Description>
                </VStack>
                <LegacyDialog.CloseTrigger asChild>
                  <IconButton aria-label="Close" variant="ghost" size="sm">
                    <Cancel01Icon size={16} />
                  </IconButton>
                </LegacyDialog.CloseTrigger>
              </HStack>

              {/* Info banner */}
              <HStack
                p="3"
                bg="bg.subtle"
                borderRadius="md"
                gap="2"
                alignItems="flex-start"
              >
                <InformationCircleIcon size={16} />
                <VStack gap="1" alignItems="flex-start" flex="1">
                  <Text fontSize="xs" fontWeight="medium">
                    Proxy-Level Filtering
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    These rules filter requests BEFORE they&apos;re recorded,
                    unlike display filters which only hide requests in the UI.
                    Use this for high-frequency polling requests that flood your
                    history.
                  </Text>
                </VStack>
              </HStack>

              {/* Body */}
              {editingRule ? (
                <RuleEditor
                  rule={editingRule}
                  isCreating={isCreating}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              ) : (
                <VStack gap="3" alignItems="stretch">
                  {/* Create button */}
                  <Button variant="outline" size="sm" onClick={handleCreate}>
                    <Add01Icon size={14} />
                    Create New Rule
                  </Button>

                  {/* Rules list */}
                  {rules.length === 0 ? (
                    <Box p="4" textAlign="center">
                      <Text fontSize="sm" color="fg.muted">
                        No exclusion rules configured
                      </Text>
                    </Box>
                  ) : (
                    <VStack
                      gap="2"
                      alignItems="stretch"
                      maxH="400px"
                      overflowY="auto"
                    >
                      {rules.map((rule) => (
                        <RuleItem
                          key={rule.id}
                          rule={rule}
                          onToggle={handleToggle}
                          onEdit={handleEdit}
                          onDuplicate={handleDuplicate}
                          onDelete={handleDelete}
                        />
                      ))}
                    </VStack>
                  )}
                </VStack>
              )}
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}

type RuleItemProps = {
  rule: ExclusionRule;
  onToggle: (rule: ExclusionRule) => void;
  onEdit: (rule: ExclusionRule) => void;
  onDuplicate: (rule: ExclusionRule) => void;
  onDelete: (rule: ExclusionRule) => void;
};

function RuleItem({
  rule,
  onToggle,
  onEdit,
  onDuplicate,
  onDelete,
}: RuleItemProps) {
  return (
    <HStack
      p="3"
      borderWidth="1px"
      borderColor={rule.enabled ? 'border.default' : 'border.subtle'}
      borderRadius="md"
      gap="3"
      bg={rule.enabled ? 'bg.default' : 'bg.subtle'}
      opacity={rule.enabled ? 1 : 0.7}
    >
      <Switch
        checked={rule.enabled}
        onCheckedChange={() => onToggle(rule)}
        size="sm"
      />

      <VStack gap="1" alignItems="flex-start" flex="1" minW="0">
        <HStack gap="2" alignItems="center">
          <Text fontSize="sm" fontWeight="medium" truncate>
            {rule.name}
          </Text>
          <Badge size="sm" variant="fill">
            {rule.type}
          </Badge>
          {rule.isRegex && (
            <Badge size="sm" variant="outline">
              regex
            </Badge>
          )}
        </HStack>
        {rule.description && (
          <Text fontSize="xs" color="fg.muted" truncate w="full">
            {rule.description}
          </Text>
        )}
        <Text
          fontSize="xs"
          fontFamily="mono"
          color="fg.subtle"
          truncate
          w="full"
        >
          {rule.pattern}
        </Text>
      </VStack>

      <HStack gap="1" flexShrink={0}>
        <Tooltip title="Edit rule">
          <IconButton
            aria-label="Edit"
            size="xs"
            variant="ghost"
            onClick={() => onEdit(rule)}
          >
            <Edit02Icon size={12} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Duplicate rule">
          <IconButton
            aria-label="Duplicate"
            size="xs"
            variant="ghost"
            onClick={() => onDuplicate(rule)}
          >
            <Copy01Icon size={12} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete rule">
          <IconButton
            aria-label="Delete"
            size="xs"
            variant="ghost"
            onClick={() => onDelete(rule)}
          >
            <Delete02Icon size={12} />
          </IconButton>
        </Tooltip>
      </HStack>
    </HStack>
  );
}

type RuleEditorProps = {
  rule: ExclusionRule;
  isCreating: boolean;
  onSave: (rule: ExclusionRule) => void;
  onCancel: () => void;
};

function RuleEditor({ rule, isCreating, onSave, onCancel }: RuleEditorProps) {
  const [name, setName] = useState(rule.name);
  const [description, setDescription] = useState(rule.description || '');
  const [type, setType] = useState<'include' | 'exclude'>(rule.type);
  const [pattern, setPattern] = useState(rule.pattern);
  const [isRegex, setIsRegex] = useState(rule.isRegex);
  const [enabled, setEnabled] = useState(rule.enabled);
  const [patternError, setPatternError] = useState<string | null>(null);
  // Validate regex pattern
  const validatePattern = useCallback((value: string, useRegex: boolean) => {
    if (!value.trim()) {
      setPatternError('Pattern is required');

      return false;
    }

    if (useRegex) {
      try {
        new RegExp(value);
        setPatternError(null);

        return true;
      } catch (e) {
        setPatternError(
          `Invalid regex: ${e instanceof Error ? e.message : 'Unknown error'}`,
        );

        return false;
      }
    }

    setPatternError(null);

    return true;
  }, []);
  const handlePatternChange = useCallback(
    (value: string) => {
      setPattern(value);
      validatePattern(value, isRegex);
    },
    [isRegex, validatePattern],
  );
  const handleRegexChange = useCallback(
    (useRegex: boolean) => {
      setIsRegex(useRegex);
      validatePattern(pattern, useRegex);
    },
    [pattern, validatePattern],
  );
  const canSave = name.trim() !== '' && pattern.trim() !== '' && !patternError;
  const handleSave = useCallback(() => {
    if (!canSave) return;
    if (!validatePattern(pattern, isRegex)) return;
    onSave({
      ...rule,
      name: name.trim(),
      description: description.trim(),
      type,
      pattern: pattern.trim(),
      isRegex,
      enabled,
    });
  }, [
    canSave,
    name,
    description,
    type,
    pattern,
    isRegex,
    enabled,
    rule,
    onSave,
    validatePattern,
  ]);

  return (
    <VStack gap="4" alignItems="stretch">
      <Text fontSize="lg" fontWeight="medium">
        {isCreating ? 'Create New Rule' : 'Edit Rule'}
      </Text>

      <VStack gap="3" alignItems="stretch">
        <Box>
          <Text fontSize="xs" fontWeight="medium" mb="1">
            Name
          </Text>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Hide Price Polling"
            size="sm"
          />
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="medium" mb="1">
            Description (optional)
          </Text>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this rule filter?"
            size="sm"
          />
        </Box>

        <Box>
          <Text fontSize="xs" fontWeight="medium" mb="1">
            Rule Type
          </Text>
          <RadioGroup.Root
            value={type}
            onValueChange={(details) =>
              setType(details.value as 'include' | 'exclude')
            }
            size="sm"
          >
            <HStack gap="4">
              <RadioGroup.Item value="exclude">
                <RadioGroup.ItemControl />
                <RadioGroup.ItemText>
                  <Text fontSize="xs">Exclude (hide matching)</Text>
                </RadioGroup.ItemText>
                <RadioGroup.ItemHiddenInput />
              </RadioGroup.Item>
              <RadioGroup.Item value="include">
                <RadioGroup.ItemControl />
                <RadioGroup.ItemText>
                  <Text fontSize="xs">Include (show only matching)</Text>
                </RadioGroup.ItemText>
                <RadioGroup.ItemHiddenInput />
              </RadioGroup.Item>
            </HStack>
          </RadioGroup.Root>
        </Box>

        <Box>
          <HStack justify="space-between" mb="1">
            <Text fontSize="xs" fontWeight="medium">
              Pattern
            </Text>
            <HStack gap="2" alignItems="center">
              <Text fontSize="xs" color="fg.muted">
                Regex
              </Text>
              <Switch
                checked={isRegex}
                onCheckedChange={(details) =>
                  handleRegexChange(details.checked)
                }
                size="sm"
              />
            </HStack>
          </HStack>
          <Textarea
            value={pattern}
            onChange={(e) => handlePatternChange(e.target.value)}
            placeholder={
              isRegex
                ? 'e.g., /api/prices|/poll|/heartbeat'
                : 'e.g., /api/prices'
            }
            rows={2}
            fontFamily="mono"
            fontSize="sm"
            borderColor={patternError ? 'border.error' : undefined}
          />
          {patternError && (
            <HStack gap="1" mt="1" color="fg.error">
              <Alert01Icon size={12} />
              <Text fontSize="xs">{patternError}</Text>
            </HStack>
          )}
          <Text fontSize="xs" color="fg.muted" mt="1">
            {isRegex
              ? 'Use regex pattern to match URLs. E.g., /api/v[0-9]+/prices'
              : 'Use simple substring match. E.g., /prices will match any URL containing "/prices"'}
          </Text>
        </Box>

        <HStack gap="2" alignItems="center">
          <Switch
            checked={enabled}
            onCheckedChange={(details) => setEnabled(details.checked)}
            size="sm"
          />
          <Text fontSize="xs">Enable rule immediately</Text>
        </HStack>
      </VStack>

      <HStack gap="2" justify="flex-end">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {isCreating ? 'Create' : 'Save'}
        </Button>
      </HStack>
    </VStack>
  );
}
